/* The Sandbox trace of one turn, for super admins: every LLM call the turn made, in
   order, with the exact messages sent, the call parameters and the raw response. */

import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { SandboxLlmCall } from '../../../../api/client';
import { LoadingBlock } from '../../../Spinner';
import { ErrorLine, Icon } from '../kit';
import { font as f } from '../style';
import { C } from '../tokens';

const MONO = '400 12px/1.5 var(--font-mono)';

/** Text for a message or response: JSON (most node outputs) is pretty-printed, which
 *  also turns escaped \uXXXX sequences back into the characters the model wrote. */
function asText(v: unknown): string {
  if (v == null) return '';
  if (typeof v !== 'string') return JSON.stringify(v, null, 2);
  const s = v.trim();
  if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
    try { return JSON.stringify(JSON.parse(s), null, 2); } catch { /* not JSON after all */ }
  }
  return v;
}

export function TraceDrawer({ accountId, fanId, turnId, onClose }: { accountId: string; fanId: string; turnId: string; onClose: () => void }) {
  const { t } = useTranslation('assistantCompact');
  const [calls, setCalls] = useState<SandboxLlmCall[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<number>>(new Set());

  useEffect(() => {
    let live = true;
    setCalls(null);
    setError(null);
    api.getSandboxTurnTrace(accountId, fanId, turnId).then(
      (r) => { if (live) { setCalls(r.calls); setOpen(new Set(r.calls.length === 1 ? [r.calls[0]!.id] : [])); } },
      (e) => { if (live) setError(e instanceof Error ? e.message : String(e)); },
    );
    return () => { live = false; };
  }, [accountId, fanId, turnId]);

  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  const sum = (pick: (c: SandboxLlmCall) => number | null | undefined) => (calls ?? []).reduce((s, c) => s + (pick(c) ?? 0), 0);
  const toggle = (id: number) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const all = calls ? calls.every((c) => open.has(c.id)) : false;

  return createPortal(
    <div className="cpt-root" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'color-mix(in srgb, var(--om-page) 60%, transparent)', display: 'flex', justifyContent: 'flex-end' }}>
      <div role="dialog" aria-modal="true" aria-label={t('sb.trace.title')} style={{ width: 760, maxWidth: '100%', height: '100%', background: C.surface, borderLeft: `1px solid ${C.borderHi}`, boxShadow: '-16px 0 48px rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 10px 12px 16px', borderBottom: `1px solid ${C.divider}` }}>
          <Icon name="code" size={15} color={C.purpleLt} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
            <span style={{ font: f(600, 14), color: C.text }}>{t('sb.trace.title')}</span>
            <span style={{ font: f(400, 11.5), color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {calls
                ? [t('sb.trace.calls', { count: calls.length }), t('sb.trace.tokens', { in: sum((c) => c.prompt_tokens).toLocaleString(), out: sum((c) => c.completion_tokens).toLocaleString() }), seconds(sum((c) => c.latency_ms))].join(' · ')
                : ' '}
            </span>
          </div>
          <CopyBtn text={turnId} label={t('sb.trace.copyTurn')} />
          {calls && calls.length > 1 && (
            <button type="button" onClick={() => setOpen(all ? new Set() : new Set(calls.map((c) => c.id)))} className="cpt-hover" style={{ height: 28, padding: '0 10px', border: 0, borderRadius: 6, background: 'transparent', color: C.purpleLt, font: f(500, 12), cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {all ? t('sb.trace.collapseAll') : t('sb.trace.expandAll')}
            </button>
          )}
          <button type="button" onClick={onClose} title={t('close')} aria-label={t('close')} className="cpt-hover-row" style={{ width: 28, height: 28, border: 0, borderRadius: 6, background: 'transparent', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="x" size={15} />
          </button>
        </div>

        <div className="cpt-thin-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {error && <ErrorLine>{error}</ErrorLine>}
          {!calls && !error && <LoadingBlock />}
          {calls && !calls.length && <div style={{ padding: 24, textAlign: 'center', font: f(400, 12.5), color: C.muted }}>{t('sb.trace.empty')}</div>}
          {calls?.map((c) => <CallCard key={c.id} call={c} open={open.has(c.id)} onToggle={() => toggle(c.id)} />)}
        </div>
      </div>
    </div>,
    document.body,
  );
}

const seconds = (ms: number) => `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)}s`;

function CallCard({ call, open, onToggle }: { call: SandboxLlmCall; open: boolean; onToggle: () => void }) {
  const { t } = useTranslation('assistantCompact');
  const served = call.served_model && call.served_model !== call.requested_model ? call.served_model : null;
  const resp = call.response ?? {};
  return (
    <div style={{ background: C.card, border: `1px solid ${call.error ? C.tintLine45 : C.border}`, borderRadius: 8, overflow: 'hidden', flex: 'none' }}>
      <button type="button" onClick={onToggle} className="cpt-hover-soft" style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: 0, background: 'transparent', cursor: 'pointer', textAlign: 'left' }}>
        <Icon name="chevron-right" size={14} color={C.sub} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', flex: 'none' }} />
        <span style={{ flex: 'none', minWidth: 22, font: f(600, 11.5), color: C.muted, fontVariantNumeric: 'tabular-nums' }}>#{call.seq ?? '–'}</span>
        <span style={{ flex: 1, minWidth: 0, font: f(600, 13), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={call.node ?? ''}>{call.node || '—'}</span>
        <span title={served ? t('sb.trace.servedBy', { model: served }) : undefined} style={{ flex: '0 1 auto', minWidth: 0, font: f(400, 11.5), color: C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {call.requested_model || '?'}{served ? ` → ${served}` : ''}
        </span>
        <Meta>{t('sb.trace.callTokens', { in: (call.prompt_tokens ?? 0).toLocaleString(), out: (call.completion_tokens ?? 0).toLocaleString() })}</Meta>
        <Meta>{seconds(call.latency_ms ?? 0)}</Meta>
        {call.error && <span style={{ flex: 'none', padding: '1px 7px', borderRadius: 99, background: C.tint20, font: f(600, 11), color: C.purplePale }}>{t('sb.trace.error')}</span>}
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 12px 12px', borderTop: `1px solid ${C.divider}` }}>
          {call.error && <Block label={t('sb.trace.error')}><Pre text={call.error} /></Block>}
          <Block label={t('sb.trace.request', { count: call.request_messages.length })} copy={asText(call.request_messages)}>
            {call.request_messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ padding: '1px 7px', borderRadius: 4, background: m.role === 'system' ? C.tint16 : m.role === 'assistant' ? 'var(--om-divider)' : C.input, border: `1px solid ${C.border}`, font: f(600, 10.5), letterSpacing: '.04em', textTransform: 'uppercase', color: m.role === 'system' ? C.purplePale : C.sub }}>{m.role || '?'}</span>
                  {m.name && <span style={{ font: f(400, 11), color: C.muted }}>{m.name}</span>}
                  <span style={{ font: f(400, 11), color: C.muted }}>{t('sb.trace.chars', { count: asText(m.content).length })}</span>
                  <span style={{ flex: 1 }} />
                  <CopyBtn text={asText(m.content)} label={t('sb.trace.copy')} small />
                </div>
                <Pre text={asText(m.content)} />
                {m.tool_calls ? <Pre text={asText(m.tool_calls)} /> : null}
              </div>
            ))}
          </Block>
          <Block label={t('sb.trace.response')} copy={asText(resp.content)}>
            <Pre text={asText(resp.content) || '—'} />
            {resp.tool_calls ? <Pre text={asText(resp.tool_calls)} /> : null}
          </Block>
          {resp.reasoning && <Fold label={t('sb.trace.reasoning')}><Pre text={resp.reasoning} /></Fold>}
          {call.params && <Fold label={t('sb.trace.params')}><Pre text={asText(call.params)} /></Fold>}
        </div>
      )}
    </div>
  );
}

function Meta({ children }: { children: ReactNode }) {
  return <span style={{ flex: 'none', font: f(500, 11.5), color: C.muted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{children}</span>;
}

function Block({ label, copy, children }: { label: string; copy?: string; children: ReactNode }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ font: f(600, 10.5), letterSpacing: '.08em', textTransform: 'uppercase', color: C.muted }}>{label}</span>
        <span style={{ flex: 1 }} />
        {copy !== undefined && <CopyBtn text={copy} label={t('sb.trace.copy')} small />}
      </div>
      {children}
    </div>
  );
}

function Fold({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="cpt-hover" style={{ alignSelf: 'flex-start', height: 24, display: 'flex', alignItems: 'center', gap: 4, padding: '0 8px 0 4px', border: 0, borderRadius: 6, background: 'transparent', color: C.purpleLt, font: f(600, 10.5), letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer' }}>
        <Icon name="chevron-right" size={12} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
        {label}
      </button>
      {open && children}
    </div>
  );
}

function Pre({ text }: { text: string }) {
  return (
    <pre className="cpt-thin-scroll" style={{ margin: 0, maxHeight: 360, overflow: 'auto', padding: '8px 10px', background: C.input, border: `1px solid ${C.border}`, borderRadius: 6, font: MONO, color: C.body, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{text}</pre>
  );
}

function CopyBtn({ text, label, small }: { text: string; label: string; small?: boolean }) {
  const { t } = useTranslation('assistantCompact');
  const [done, setDone] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(text).then(() => { setDone(true); window.setTimeout(() => setDone(false), 1200); }, () => {});
  };
  return (
    <button type="button" onClick={copy} title={label} aria-label={label} className="cpt-hover" style={{ height: small ? 22 : 28, padding: small ? '0 6px' : '0 9px', display: 'flex', alignItems: 'center', gap: 4, border: 0, borderRadius: 6, background: 'transparent', color: done ? C.purplePale : C.sub, font: f(500, small ? 11 : 12), cursor: 'pointer', whiteSpace: 'nowrap', flex: 'none' }}>
      <Icon name={done ? 'check' : 'copy'} size={small ? 11 : 13} />
      {done ? t('sb.trace.copied') : small ? null : label}
    </button>
  );
}
